import './Collage.sass';

import React, { FC, useEffect, useMemo, useRef } from 'react';
import { CollageProps } from './Collage.interface';
import { IcGallery28, IcPlay28, IcVideo28 } from '$assets';
import { classNames as cn, getFormattedTime } from '$utils';
import { useDesktop, usePhotoUrl, useThumbHash } from '$hooks';
import { cancelPreloadMedia, preloadMedia } from '../../../services/mediaLoader';

const compile = (photo, width, height) => ({
  id: photo.photoId || photo.videoId,
  width: parseInt(width),
  height: parseInt(height),
  image: {
    ...photo,
    src: photo.photoMedium,
    width: photo.width,
    height: photo.height,
  },
  orig: photo,
  moreItems: 0,
});

const getMaxHeight = (thumbs, maxHeight) => {
  const max = thumbs
    .map((thumb) => thumb.height)
    .reduce((item, current) => {
      if (item > current) {
        return item;
      } else {
        return current;
      }
    }, 0);

  return Math.min(maxHeight, max);
};

const computeRatios = (thumbs) => thumbs.map((thumb) => thumb.width / thumb.height);

function cropRatios(ratios: number[], avgRatio: number): number[] {
  if (avgRatio > 1.1) {
    return ratios.map((ratio) => Math.max(1.0, ratio));
  }
  return ratios.map((ratio) => Math.min(1.0, ratio));
}

const multiThumbsHeight = (ratios, width, margin) =>
  Math.floor((width - (ratios.length - 1) * margin) / sum(ratios));

const sum = (items) => items.reduce((item, value) => value + item, 0);

function calculateLayouts(
  ratiosCropped: number[],
  cnt: number,
  maxWidth: number,
  margin: number,
) {
  const tries: Record<string, number[]> = {};

  tries[cnt.toString()] = [multiThumbsHeight(ratiosCropped, maxWidth, margin)];

  for (let firstLine = 1; firstLine <= cnt - 1; firstLine++) {
    const secondLine = cnt - firstLine;
    tries[`${firstLine},${secondLine}`] = [
      multiThumbsHeight(ratiosCropped.slice(0, firstLine), maxWidth, margin),
      multiThumbsHeight(ratiosCropped.slice(firstLine), maxWidth, margin),
    ];
  }

  for (let firstLine = 1; firstLine <= cnt - 2; firstLine++) {
    for (let secondLine = 1; secondLine <= cnt - firstLine - 1; secondLine++) {
      const thirdLine = cnt - firstLine - secondLine;
      tries[`${firstLine},${secondLine},${thirdLine}`] = [
        multiThumbsHeight(ratiosCropped.slice(0, firstLine), maxWidth, margin),
        multiThumbsHeight(
          ratiosCropped.slice(firstLine, firstLine + secondLine),
          maxWidth,
          margin,
        ),
        multiThumbsHeight(ratiosCropped.slice(firstLine + secondLine), maxWidth, margin),
      ];
    }
  }

  return tries;
}

export function Photo({
  item,
  children,
  decryptKey,
  isHidden,
  isBlocked,
  isVisible,
  isBlured = false,
}: any) {
  const src = usePhotoUrl(item.image, 'photoMedium', decryptKey);
  const { thumbnail, thumbHash } = item?.image ?? {};
  const placeholder = useThumbHash(thumbHash, thumbnail);
  const srcSet: string[] = [];

  src && !isHidden && srcSet.push(src);
  !!placeholder && srcSet.push(placeholder);

  useEffect(() => {
    if (src && !isHidden && !isVisible) {
      preloadMedia(src, 'photo');

      return () => {
        cancelPreloadMedia(src);
      };
    }
  }, [src, isHidden, isVisible]);

  return (
    <div className="Collage__item__image__wrap">
      <div
        className={cn('Collage__item__image', { empty: !srcSet.length, blur: isBlured })}
        style={{
          backgroundImage: `${
            isBlocked
              ? 'linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)),'
              : ''
          } ${srcSet.map((s) => `url(${s})`).join()}`,
        }}
      />
      <div className="Collage__item__image__cont">{children}</div>
    </div>
  );
}

export const Collage: FC<CollageProps> = ({
  items,
  renderExtra = null,
  maxWidth = 600,
  decryptKey,
  isHidden = false,
  ratio = 1.2,
  isBlocked,
  isVisible = false,
  cropTen = false,
}) => {
  const isDesktop = useDesktop();
  const ref = useRef<HTMLDivElement | null>(null);

  const collage = useMemo(() => {
    const computeSingleItem = (thumb, maxWidth, maxHeight, ratio) => {
      let tWidth, tHeight;
      if (ratio >= maxWidth / maxHeight) {
        tWidth = Math.round(Math.min(maxWidth, thumb.width));
        tHeight = Math.round(Math.min(tWidth / ratio, maxHeight));
      } else {
        tHeight = Math.round(Math.min(maxHeight, thumb.height));
        tWidth = Math.round(Math.min(tHeight * ratio, maxWidth));
      }
      return {
        thumbs: [compile(thumb, tWidth, tHeight)],
        width: tWidth,
        height: tHeight,
      };
    };

    const computeTwoItems = (thumbs, ratios, maxWidth, maxHeight, margin) => {
      const w0 = Math.ceil(
        (maxWidth - margin) / ratios[1] / (1 / ratios[0] + 1 / ratios[1]),
      );
      const w1 = maxWidth - w0 - margin;
      const h = Math.ceil(Math.min(maxHeight, w0 / ratios[0], w1 / ratios[1]));

      return {
        thumbs: [compile(thumbs[0], w0, h), compile(thumbs[1], w1, h)],
        width: maxWidth + margin,
        height: h + margin,
      };
    };

    const computeThreeItems = (thumbs, ratios, maxWidth, maxHeight, margin) => {
      let h = getMaxHeight(thumbs, maxHeight);
      const coverWidth = Math.floor(Math.min(h * ratios[0], (maxWidth - margin) * 0.75));
      const h1 = Math.floor((ratios[1] * (h - margin)) / (ratios[2] + ratios[1]));
      const h0 = h - h1 - margin;
      const w = Math.floor(
        Math.min(maxWidth - coverWidth - margin, h1 * ratios[2], h0 * ratios[1]),
      );

      return {
        thumbs: [
          compile(thumbs[0], coverWidth, h),
          compile(thumbs[1], w, h0),
          compile(thumbs[2], w, h1),
        ],
        width: coverWidth + w + margin * 2,
        height: h + margin * 2,
      };
    };

    const computeFourItems = (thumbs, ratios, maxWidth, maxHeight, margin) => {
      let h = getMaxHeight(thumbs, maxHeight);
      const coverWidth = Math.floor(Math.min(h * ratios[0], (maxWidth - margin) * 0.66));
      let w = Math.floor(
        (maxHeight - 2 * margin) / (1 / ratios[1] + 1 / ratios[2] + 1 / ratios[3]),
      );

      const h0 = Math.floor(
        (ratios[1] * (h - margin)) / (ratios[2] + ratios[1] + ratios[3]),
      );
      const h1 = Math.floor(h - h0 * 2 - margin);
      const h2 = Math.floor(h - h0 * 2 - margin);
      w = Math.floor(Math.min(maxWidth - coverWidth - margin, w));

      return {
        thumbs: [
          compile(thumbs[0], coverWidth, h),
          compile(thumbs[1], w, h0),
          compile(thumbs[2], w, h1),
          compile(thumbs[3], w, h2),
        ],
        width: coverWidth + w + margin * 2,
        height: h + margin * 2,
      };
    };

    const computeMultipleItems = (thumbs, ratios, maxWidth, maxHeight, margin) => {
      const avgRatio = sum(ratios) / ratios.length;
      const ratiosCropped = cropRatios(ratios, avgRatio);

      const tries = calculateLayouts(ratiosCropped, thumbs.length, maxWidth, margin);

      let optConf: string | null = null;
      let optDiff: number = 0;
      let optH: number = 0;

      for (let conf in tries) {
        const heights = tries[conf];
        const confH = sum(heights) + margin * (heights.length - 1);
        let confDiff = Math.abs(confH - maxHeight);

        if (conf.indexOf(',') != -1) {
          let confNums = conf.split(',').map((item) => +item);
          if (confNums[0] > confNums[1] || (confNums[2] && confNums[1] > confNums[2])) {
            confDiff += 50;
            confDiff *= 1.5;
          }
        }
        if (optConf == null || confDiff < optDiff) {
          optConf = conf;
          optDiff = confDiff;
          optH = confH;
        }
      }

      const thumbsRemain = [...thumbs];
      const ratiosRemain = [...ratiosCropped];
      const chunks = optConf!.split(',').map((item) => +item);
      const optHeights = tries[optConf!];

      const result: any = [];
      for (let i = 0; i < chunks.length; i++) {
        const lineChunksNum = chunks[i];
        const lineThumbs = thumbsRemain.splice(0, lineChunksNum);
        const lineHeight = optHeights.shift();
        let widthRemains = maxWidth;

        for (let j = 0; j < lineThumbs.length; j++) {
          const thumb = lineThumbs[j];
          const thumbRatio = ratiosRemain.shift();
          let thumbWidth;
          if (lineThumbs.length - 1 === j) {
            thumbWidth = Math.ceil(widthRemains);
          } else {
            if (!thumbRatio || !lineHeight) {
              console.warn('thumbRatio or lineHeight is null', thumbRatio, lineHeight);
              continue;
            }
            thumbWidth = Math.floor(thumbRatio * lineHeight);
            widthRemains -= thumbWidth + margin;
          }

          result.push(compile(thumb, thumbWidth, lineHeight));
        }
      }

      return {
        thumbs: result,
        width: maxWidth + margin,
        height: optH + margin,
      };
    };

    const computeMoreTenItems = (thumbs, ratios, maxWidth, maxHeight, margin) => {
      const firstTen = computeMultipleItems(
        thumbs.slice(0, 10),
        ratios.slice(0, 10),
        maxWidth,
        maxHeight,
        margin,
      );
      firstTen.thumbs[9].moreItems = thumbs.length - 9;

      return firstTen;
    };

    const thumbs = items.map((item) => item[item.type]);
    const ratios = computeRatios(thumbs);
    const maxHeight = Math.ceil(maxWidth * (isDesktop ? 1 : ratio));

    let result: { thumbs: any; width: any; height: any };
    if (thumbs.length === 1) {
      result = computeSingleItem(thumbs[0], maxWidth, maxHeight, ratios[0]);
    } else if (thumbs.length === 2) {
      result = computeTwoItems(thumbs, ratios, maxWidth, maxHeight, 0);
    } else if (thumbs.length === 3) {
      result = computeThreeItems(thumbs, ratios, maxWidth, maxHeight, 0);
    } else if (thumbs.length === 4) {
      result = computeFourItems(thumbs, ratios, maxWidth, maxHeight, 0);
    } else if (thumbs.length > 10 && cropTen) {
      result = computeMoreTenItems(thumbs, ratios, maxWidth, maxHeight, 0);
    } else {
      result = computeMultipleItems(thumbs, ratios, maxWidth, maxHeight, 0);
    }

    result.thumbs = result.thumbs.map((item) => ({
      ...item,
      width: (item.width * 100) / result.width,
      height: (item.height * 100) / result.height,
    }));

    return {
      thumbs: result.thumbs,
      width: result.width,
      height: result.height,
    };
  }, [items, maxWidth]);

  function renderItemCont(item: any) {
    const { videoId, isVideo, duration } = item;

    if (videoId > 0 || isVideo) {
      return (
        <>
          {!isBlocked && (
            <div className="ExternalVideo__iframe">
              <div className="ExternalVideo__play_btn">
                <IcPlay28 />
              </div>
            </div>
          )}
          {duration && (
            <div className="ExternalVideo__duration_label">
              {getFormattedTime(duration)}
            </div>
          )}
        </>
      );
    }
  }

  const isSingleHorizontalPhoto =
    (ratio === 1 || ratio === 1.6) &&
    collage.thumbs.length === 1 &&
    collage.thumbs[0].width < maxWidth;

  return (
    <div
      className="Collage_wrap"
      ref={ref}
      style={{
        width: ratio === 1.6 ? Math.min(maxWidth, collage.width) : 'auto',
        paddingTop: `${Math.min((collage.height / collage.width) * 100, ratio * 100)}%`,
      }}
    >
      {isSingleHorizontalPhoto && (
        <div className="Collage__bg_helper">
          <div
            className="Collage__bg_helper__image"
            style={{ backgroundImage: `url(${collage.thumbs[0].image.thumbnail})` }}
          />
        </div>
      )}

      <div
        className="Collage"
        style={{
          width: `calc(${(collage.width / maxWidth) * 100}% + 4px)`,
          height: 'calc(100% + 4px)',
        }}
      >
        {/* @ts-ignore */}
        {collage.thumbs.map((item, i) => {
          return (
            <div
              key={i}
              className={cn({
                Collage__item: true,
                [item.orig?.videoId > 0 || item.orig?.isVideo ? 'video' : 'photo']: true,
              })}
              style={{
                width: `${item.width}%`,
                height: `${item.height}%`,
              }}
              /* @ts-ignore */
              data-id={item.id}
              data-part={i}
            >
              <div className="Collage__item__cont">
                {(isBlocked || item.moreItems > 0) &&
                  (item.moreItems > 0 ? (
                    <div className="Collage__mediaMore">+{item.moreItems}</div>
                  ) : (
                    <div className="Collage__mediaIcon">
                      {item.orig?.videoId > 0 || item.orig?.isVideo ? (
                        <IcVideo28 />
                      ) : (
                        <IcGallery28 />
                      )}
                    </div>
                  ))}

                <Photo
                  item={item}
                  decryptKey={decryptKey}
                  isHidden={isHidden}
                  isBlocked={isBlocked}
                  isVisible={isVisible}
                  isBlured={item.moreItems > 0}
                >
                  {renderItemCont(item.orig)}
                  {renderExtra && renderExtra(i)}
                </Photo>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
